import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  fetchMe,
  fetchPermissions,
  logout as apiLogout,
  setAccessToken,
  setUnauthorizedHandler,
  tryRefresh,
} from "../lib/api";
import type { MeResponse, TokenResponse } from "../lib/types";

type SessionStatus = "loading" | "anonymous" | "authenticated";

interface SessionValue {
  status: SessionStatus;
  me: MeResponse | null;
  permissions: string[];
  completeLogin: (tokens: TokenResponse) => Promise<void>;
  signOut: (allSessions?: boolean) => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);

  const reset = useCallback(() => {
    setAccessToken(null);
    setMe(null);
    setPermissions([]);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(reset);
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [reset]);

  const loadProfile = useCallback(async () => {
    const [profile, perms] = await Promise.all([fetchMe(), fetchPermissions()]);
    setMe(profile);
    setPermissions(perms.permissions);
    setStatus("authenticated");
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const token = await tryRefresh();
      if (cancelled) {
        return;
      }
      if (token === null) {
        setStatus("anonymous");
        return;
      }
      try {
        await loadProfile();
      } catch {
        if (!cancelled) {
          reset();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadProfile, reset]);

  const completeLogin = useCallback(
    async (tokens: TokenResponse) => {
      if (tokens.access_token !== undefined) {
        setAccessToken(tokens.access_token);
      }
      await loadProfile();
    },
    [loadProfile],
  );

  const signOut = useCallback(
    async (allSessions = false) => {
      try {
        await apiLogout(allSessions);
      } catch {
        // The local session is cleared even if the server call fails.
      }
      reset();
    },
    [reset],
  );

  const hasPermission = useCallback(
    (permission: string) => permissions.includes(permission),
    [permissions],
  );

  const value = useMemo<SessionValue>(
    () => ({ status, me, permissions, completeLogin, signOut, hasPermission }),
    [status, me, permissions, completeLogin, signOut, hasPermission],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return value;
}
